import useReveal from "@/hooks/useReveal";
import { useLang } from "@/i18n/LangContext";
import { Check } from "lucide-react";

const DEVICES = [
  { key: "remote", img: "/control/remote.png" },
  { key: "wall", img: "/control/wall-switch.png" },
  { key: "app", img: "/control/mobile-app.png" },
];

function DeviceCard({ device, index }) {
  const { t } = useLang();
  const { ref, className } = useReveal();
  const d = t(`technology.devices.${device.key}`);

  return (
    <article
      ref={ref}
      data-testid={`control-device-${device.key}`}
      className={`${className} group flex flex-col`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Image, no frame */}
      <div className="relative aspect-[4/5] max-w-[280px] md:max-w-none mx-auto w-full">
        <img
          src={device.img}
          alt={d.name}
          draggable="false"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 ease-out group-hover:scale-[1.04]"
        />
      </div>

      <div className="mt-5">
        <span
          data-testid={`control-device-${device.key}-tag`}
          className="inline-flex items-center gap-1.5 text-[10px] font-body font-semibold tracking-[0.22em] uppercase text-[#86868B]"
        >
          <Check className="w-3 h-3" strokeWidth={2.5} />
          {d.tag}
        </span>
        <h3
          data-testid={`control-device-${device.key}-name`}
          className="mt-2.5 font-display font-light tracking-tight text-xl md:text-2xl text-[#1D1D1F] leading-[1.1]"
        >
          {d.name}
        </h3>
        <p className="mt-3 font-body text-[14px] text-[#1D1D1F]/65 leading-relaxed">
          {d.description}
        </p>
      </div>
    </article>
  );
}

export default function Technology() {
  const { t } = useLang();
  const { ref, className } = useReveal();
  return (
    <section
      id="technology"
      data-testid="technology-section"
      className="pt-10 md:pt-12 lg:pt-14 pb-10 md:pb-12 lg:pb-14 bg-[#FAFAFA]"
    >
      {/* Thin divider matching the one above the inquiry section */}
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          data-testid="control-divider"
          className="h-px bg-[#1D1D1F]/10 mb-14 md:mb-20"
        />
      </div>
      <div ref={ref} className={`${className} max-w-6xl mx-auto px-6 md:px-10`}>
        <div className="max-w-3xl mb-10 md:mb-14">
          <h2
            data-testid="control-title"
            className="font-display font-light tracking-tight text-3xl md:text-4xl lg:text-5xl text-[#1D1D1F] leading-[1.05]"
          >
            {t("technology.title")}
          </h2>
        </div>

        <div
          data-testid="control-devices-grid"
          className="grid md:grid-cols-3 gap-x-6 lg:gap-x-10 gap-y-10 md:gap-y-14"
        >
          {DEVICES.map((d, i) => (
            <DeviceCard key={d.key} device={d} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
